import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'
import {
  isValidElement,
  useMemo,
  useContext,
  createContext,
  cloneElement,
  Children,
  forwardRef,
} from 'react'
import type { FC, HTMLAttributes, MouseEvent, PropsWithChildren } from 'react'
import { getIdWithUniqueFallback } from '@getcircuit/web-utils'

import {
  ActionDialogActions,
  StyledActionDialogError,
} from './ActionDialog.styled'
import {
  DialogAligner,
  DialogBox,
  DialogContentStyles,
  DialogOverlayStyles,
} from '../Dialog/Dialog.styled'
import type {
  ActionDialogActionProps,
  ActionDialogContentProps,
  ActionDialogContextState,
  ActionDialogProps,
} from './types'
import { useActionDialogState } from './useActionDialogState'
import type {
  DialogBoxProps,
  DialogCloseProps,
  DialogTriggerProps,
} from '../Dialog/types'
import { Dialog } from '../Dialog/Dialog'

const ActionDialogContext = createContext<ActionDialogContextState>(
  undefined as never,
)

const useActionDialogStateContext = () => {
  const ctx = useContext(ActionDialogContext)

  return ctx
}

function ActionDialog({
  children,
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...rest
}: PropsWithChildren<ActionDialogProps>) {
  const internalActionDialogState = useActionDialogState({
    open: openProp,
    defaultOpen,
    onOpenChange,
  })

  const { open, setOpen } = internalActionDialogState

  return (
    <ActionDialogContext.Provider value={internalActionDialogState}>
      <AlertDialogPrimitive.Root {...rest} open={open} onOpenChange={setOpen}>
        <AlertDialogPrimitive.Overlay css={DialogOverlayStyles} />
        {children}
      </AlertDialogPrimitive.Root>
    </ActionDialogContext.Provider>
  )
}

function ActionDialogContent({
  children,
  onEscapeKeyDown,
  ...rest
}: PropsWithChildren<ActionDialogContentProps>) {
  const { hasActionRunning } = useActionDialogStateContext()

  return (
    <AlertDialogPrimitive.Content
      css={DialogContentStyles}
      {...rest}
      onEscapeKeyDown={(e) => {
        // if action is running, prevent closing the dialog on `esc` keypress
        if (hasActionRunning) e.preventDefault()
        onEscapeKeyDown?.(e)
      }}
    >
      <DialogAligner>{children}</DialogAligner>
    </AlertDialogPrimitive.Content>
  )
}

function ActionDialogBox({
  fullscreen = 'none',
  ...rest
}: PropsWithChildren<DialogBoxProps>) {
  return <DialogBox data-fullscreen={fullscreen} {...rest} />
}

/** Wrapper for an action dialog action. It adds the necessary handles to its child button. */
function ActionDialogAction({
  children,
  onAction,
  id,
}: PropsWithChildren<ActionDialogActionProps>) {
  const internalContext = useActionDialogStateContext()

  /**  Used to ensure an unique id so we know what action triggered the handleAction */
  const memoizedId = useMemo(() => {
    return getIdWithUniqueFallback([id], 'action')
  }, [id])

  if (internalContext == null) {
    throw Error('ActionDialog.Item must be used inside an ActionDialog')
  }

  const { dispatchAction, loadingId, hasActionRunning } = internalContext

  const child = Children.only(children)

  if (!isValidElement(child)) {
    throw Error(
      'The children of an ActionDialog.Item must be a single react element',
    )
  }

  const childIsComponent = typeof child.type !== 'string'

  return (
    <>
      {cloneElement(child, {
        /**
         * Note: this is not much flexible as the action child component
         * must accept a `isLoading` props. Could be refactored to use a prop from
         * the ActionDialog context or a render function as children
         * */
        ...(childIsComponent && { isLoading: loadingId === memoizedId }),
        disabled: hasActionRunning === true ? true : child.props.disabled,
        type: child.props.type || 'button',
        id: memoizedId,
        onClick(e: MouseEvent) {
          e.preventDefault()
          dispatchAction({ handler: onAction, id: memoizedId })
        },
      })}
    </>
  )
}

/** Displays the last action handling error. */
const ActionDialogError: FC<HTMLAttributes<HTMLDivElement>> = (props) => {
  const actionDialogContext = useActionDialogStateContext()

  if (actionDialogContext == null) {
    throw Error('ActionDialog.Error must be used inside an ActionDialog')
  }

  if (actionDialogContext.error == null) {
    return null
  }

  return (
    <StyledActionDialogError {...props}>
      {actionDialogContext.error}
    </StyledActionDialogError>
  )
}

const ActionDialogClose: FC<DialogCloseProps> = ({ children, ...rest }) => {
  const actionDialogContext = useActionDialogStateContext()
  const child = Children.only(children)

  if (!isValidElement(child) || actionDialogContext == null) {
    throw new Error(
      'ActionDialog.Close must be used inside an ActionDialog and should receive a single child.',
    )
  }

  const { hasActionRunning } = actionDialogContext

  return (
    <AlertDialogPrimitive.Cancel disabled={hasActionRunning} asChild {...rest}>
      {children}
    </AlertDialogPrimitive.Cancel>
  )
}

const ActionDialogTrigger = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<DialogTriggerProps>
>(function Trigger({ children, ...rest }, ref) {
  return (
    <AlertDialogPrimitive.Trigger asChild ref={ref} {...rest}>
      {children}
    </AlertDialogPrimitive.Trigger>
  )
})

ActionDialog.Trigger = ActionDialogTrigger
ActionDialog.Close = ActionDialogClose
ActionDialog.Content = ActionDialogContent
ActionDialog.Actions = ActionDialogActions
ActionDialog.Action = ActionDialogAction
ActionDialog.Error = ActionDialogError
ActionDialog.Box = ActionDialogBox
ActionDialog.Title = Dialog.Title
ActionDialog.Description = Dialog.Description

export { ActionDialog }
